import React, { useMemo, useState } from 'react';
import { View, Text, ScrollView, TouchableOpacity, Platform } from 'react-native';
import { scale } from '@components/frame';
import { machineStatus, machineStatusColorName } from '@helpers';
import ColorWrapper from '@styles/colors';

interface EventLog {
  timestamp: string;
  eventType: string;
  data: any;
  hub?: string;
  connectionId?: string;
}

interface SignalRLogViewerProps {
  logs: EventLog[];
  searchQuery: string;
  colors: any;
  machines?: any[];
}

const SignalRLogViewerComponent: React.FC<SignalRLogViewerProps & { C: (colorName: string) => string }> = ({
  logs,
  searchQuery,
  colors,
  C,
  machines = [],
}) => {
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());

  const filteredLogs = useMemo(() => {
    if (!searchQuery.trim()) return logs;

    const query = searchQuery.toLowerCase();
    return logs.filter((log) => log.eventType.toLowerCase().includes(query) || JSON.stringify(log.data).toLowerCase().includes(query));
  }, [logs, searchQuery]);

  const toggleExpanded = (index: number) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedItems(newExpanded);
  };

  const getEventColor = (eventType: string, data?: any) => {
    if (eventType.includes('Error')) return colors.error;
    if (eventType.includes('Warning')) return colors.warning;
    if (eventType.includes('Connection')) return colors.primary;

    // Check for OnUpdatedMachineStatus in the eventType or nested data
    if (eventType.includes('OnUpdatedMachineStatus')) {
      // The actual machine status data might be in data.data (nested)
      const machineData = data?.data?.machineStatus || data?.machineStatus;
      if (machineData?.status && C) {
        const statusColorName = machineStatusColorName[machineData.status];
        if (statusColorName) {
          return C(statusColorName);
        }
      }
    }

    if (eventType.includes('SignalR:Event:')) return colors.success;
    return colors.text;
  };

  const getMachineStatusColor = (status: string) => {
    if (!C) {
      // Fallback colors if C is not available
      const fallbackColors: Record<string, string> = {
        'Active': '#65C366',
        'Change': '#FF9500',
        'Setup': '#E040FB',
        'NotProductive': '#8A8A94',
      };
      return fallbackColors[status] || colors.text;
    }
    const statusColorName = machineStatusColorName[status];
    return statusColorName ? C(statusColorName) : colors.text;
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      fractionalSecondDigits: 3,
    });
  };

  const formatEventType = (eventType: string) => {
    // Remove SignalR:Event: prefix for cleaner display
    return eventType.replace('SignalR:Event:', '');
  };

  const renderMachineStatusSummary = (data: any) => {
    // Handle nested data structure from SignalR events
    const machineData = data?.data?.machineStatus || data?.machineStatus;
    if (!machineData) return null;

    const { machineId, status, subStatus } = machineData;
    const statusColor = getMachineStatusColor(status);
    const machine = machines.find((m: any) => m.machineId === machineId);
    const machineShortName = machine?.shortName;

    return (
      <View style={{ marginTop: scale(4) }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: scale(2), flexWrap: 'wrap' }}>
          <Text style={{ fontSize: scale(11), color: colors.textSecondary, marginRight: scale(6) }}>Machine ID:</Text>
          <Text style={{ fontSize: scale(11), color: colors.text, fontWeight: '600' }}>{machineId}</Text>
          {machineShortName && <Text style={{ fontSize: scale(11), color: colors.textSecondary, marginLeft: scale(6) }}>({machineShortName})</Text>}
        </View>
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <Text style={{ fontSize: scale(11), color: colors.textSecondary, marginRight: scale(6) }}>Status:</Text>
          <View
            style={{
              backgroundColor: statusColor,
              width: scale(6),
              height: scale(6),
              borderRadius: scale(3),
              marginRight: scale(4),
            }}
          />
          <Text style={{ fontSize: scale(11), color: statusColor, fontWeight: '600' }}>{status}</Text>
          {subStatus && (
            <>
              <Text style={{ fontSize: scale(11), color: colors.textSecondary, marginHorizontal: scale(4) }}>·</Text>
              <Text style={{ fontSize: scale(11), color: colors.textSecondary }}>{subStatus}</Text>
            </>
          )}
        </View>
      </View>
    );
  };

  const formatData = (data: any) => {
    if (!data) return 'No data';

    try {
      if (typeof data === 'string') return data;
      return JSON.stringify(data, null, 2);
    } catch (e) {
      return String(data);
    }
  };

  const styles = {
    container: {
      flex: 1,
    },
    logItem: {
      marginBottom: scale(8),
      backgroundColor: colors.background,
      borderRadius: scale(8),
      borderLeftWidth: scale(3),
      overflow: 'hidden' as const,
    },
    logHeader: {
      flexDirection: 'row' as const,
      alignItems: 'center' as const,
      justifyContent: 'space-between' as const,
      padding: scale(12),
    },
    logHeaderLeft: {
      flex: 1,
      marginRight: scale(8),
    },
    eventType: {
      fontSize: scale(13),
      fontWeight: '600' as const,
      marginBottom: scale(4),
    },
    timestamp: {
      fontSize: scale(11),
      color: colors.textSecondary,
      fontFamily: Platform.OS === 'ios' ? 'Courier' : 'monospace',
    },
    expandIcon: {
      fontSize: scale(14),
      color: colors.textSecondary,
    },
    logContent: {
      padding: scale(12),
      paddingTop: 0,
      borderTopWidth: 1,
      borderTopColor: colors.border,
    },
    dataText: {
      fontSize: scale(11),
      color: colors.text,
      fontFamily: Platform.OS === 'ios' ? 'Courier' : 'monospace',
      lineHeight: scale(16),
    },
    metaInfo: {
      flexDirection: 'row' as const,
      marginTop: scale(4),
      flexWrap: 'wrap' as const,
    },
    metaTag: {
      backgroundColor: colors.panel,
      paddingHorizontal: scale(6),
      paddingVertical: scale(2),
      borderRadius: scale(4),
      marginRight: scale(6),
      marginBottom: scale(4),
    },
    metaText: {
      fontSize: scale(10),
      color: colors.textSecondary,
    },
    emptyState: {
      padding: scale(32),
      alignItems: 'center' as const,
    },
    emptyText: {
      fontSize: scale(14),
      color: colors.textSecondary,
      textAlign: 'center' as const,
    },
  };

  if (filteredLogs.length === 0) {
    return (
      <View style={styles.emptyState}>
        <Text style={styles.emptyText}>{searchQuery ? 'No events matching your search' : 'No events captured yet'}</Text>
      </View>
    );
  }

  return (
    <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
      {filteredLogs.map((log, index) => {
        const isExpanded = expandedItems.has(index);
        const isMachineStatusEvent = log.eventType.includes('OnUpdatedMachineStatus');
        const eventColor = getEventColor(log.eventType, log.data);

        return (
          <TouchableOpacity
            key={index}
            style={[
              styles.logItem,
              { borderLeftColor: eventColor },
              isMachineStatusEvent && {
                backgroundColor: colors.background, // Just use the standard background
              },
            ]}
            onPress={() => toggleExpanded(index)}
            activeOpacity={0.8}
          >
            <View style={styles.logHeader}>
              <View style={styles.logHeaderLeft}>
                <Text style={[styles.eventType, { color: eventColor }]}>{formatEventType(log.eventType)}</Text>
                <Text style={styles.timestamp}>{formatTimestamp(log.timestamp)}</Text>
                {isMachineStatusEvent && renderMachineStatusSummary(log.data)}
                {(log.hub || log.connectionId) && (
                  <View style={styles.metaInfo}>
                    {log.hub && (
                      <View style={styles.metaTag}>
                        <Text style={styles.metaText}>Hub: {log.hub}</Text>
                      </View>
                    )}
                    {log.connectionId && (
                      <View style={styles.metaTag}>
                        <Text style={styles.metaText}>Conn: {log.connectionId.substring(0, 8)}...</Text>
                      </View>
                    )}
                  </View>
                )}
              </View>
              <Text style={styles.expandIcon}>{isExpanded ? '▼' : '▶'}</Text>
            </View>

            {isExpanded && (
              <View style={styles.logContent}>
                <Text style={styles.dataText} selectable>
                  {formatData(log.data)}
                </Text>
              </View>
            )}
          </TouchableOpacity>
        );
      })}
    </ScrollView>
  );
};

const SignalRLogViewer = ColorWrapper(SignalRLogViewerComponent);
export default SignalRLogViewer;
